/**
 * SecondWorld API Client
 * 
 * Centralized API communication with WordPress REST API.
 * Updated to use SecondWorld Commerce plugin (swc/v1).
 * Uses ?rest_route= format for Docker/Apache compatibility.
 */

// Production URL - WordPress in root, React coexisting via .htaccess
const BASE_URL = 'https://secondworld.com.pk'

/**
 * Build REST API URL using wp-json format (Production compatible)
 * WordPress and React coexist - .htaccess handles routing
 */
function restUrl(route) {
    // Add /swc/v1 prefix if not already present and not a WordPress core route
    if (!route.startsWith('/swc/') && !route.startsWith('/wp/')) {
        route = `/swc/v1${route}`
    }
    // WordPress in root, API at /wp-json/
    return `${BASE_URL}/wp-json${route}`
}

/**
 * Base fetch wrapper with error handling
 */
async function fetchAPI(endpoint, options = {}) {
    const url = restUrl(endpoint)

    const config = {
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
        credentials: 'include', // For session-based cart
        ...options,
    }

    const response = await fetch(url, config)

    if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || `API Error: ${response.status}`)
    }

    return response.json()
}

/**
 * Site Configuration
 */
export async function getSiteConfig() {
    return fetchAPI('/config')
}

/**
 * Products
 */
export async function getProducts(params = {}) {
    // Filter out null/undefined values
    const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v != null)
    )
    const query = new URLSearchParams(cleanParams).toString()
    return fetchAPI(`/products${query ? `?${query}` : ''}`)
}

export async function getProduct(id) {
    return fetchAPI(`/products/${id}`)
}

export async function getProductBySlug(slug) {
    // Use NEW dedicated endpoint (bypasses cache issues)
    return fetchAPI(`/product-by-slug/${slug}`)
}

/**
 * Realtime Search
 * Debouncing should be handled on the frontend
 */
export async function searchProducts(query, limit = 10) {
    if (!query || query.length < 2) {
        return { results: [], query: '', count: 0 }
    }
    const params = new URLSearchParams({
        q: query,
        limit: limit
    }).toString()
    return fetchAPI(`/search?${params}`)
}

/**
 * Categories
 */
export async function getCategories() {
    return fetchAPI('/categories')
}

/**
 * Cart (Server-side session)
 */
export async function getCart() {
    return fetchAPI('/cart')
}

export async function addToCart(productId, quantity = 1) {
    return fetchAPI('/cart/add', {
        method: 'POST',
        body: JSON.stringify({ product_id: productId, quantity }),
    })
}

export async function updateCartItem(productId, quantity) {
    return fetchAPI('/cart/update', {
        method: 'POST',
        body: JSON.stringify({ product_id: productId, quantity }),
    })
}

export async function removeFromCart(productId) {
    return fetchAPI('/cart/remove', {
        method: 'POST',
        body: JSON.stringify({ product_id: productId }),
    })
}

export async function clearCart() {
    return fetchAPI('/cart/clear', {
        method: 'POST',
    })
}

/**
 * Checkout
 */
export async function processCheckout(orderData) {
    return fetchAPI('/checkout', {
        method: 'POST',
        body: JSON.stringify(orderData),
    })
}

/**
 * Pages (from WordPress)
 */
export async function getPage(slug) {
    return fetchAPI(`/wp/v2/pages?slug=${slug}`)
}

/**
 * Contact Form
 */
export async function submitContactForm(data) {
    return fetchAPI('/contact', {
        method: 'POST',
        body: JSON.stringify(data),
    })
}

/**
 * =============================================================================
 * MARKETPLACE PLACEHOLDERS (Future Implementation)
 * =============================================================================
 */

export async function getVendors() {
    return fetchAPI('/vendors')
}

export async function registerVendor(data) {
    return fetchAPI('/vendors/register', {
        method: 'POST',
        body: JSON.stringify(data),
    })
}

export async function getVendorDashboard(vendorId) {
    return fetchAPI(`/vendors/${vendorId}/dashboard`)
}

export async function getVendorProducts(vendorId) {
    return fetchAPI(`/vendors/${vendorId}/products`)
}

// Export API base URL
export { BASE_URL }
